// SAT catalogs needed to describe a CFDI 4.0 receptor.
//
// WHY THIS LIVES IN @vysto/shared AND NOT IN THE ENGINE. Three surfaces need
// the same list: the operator's business-profile form, the clinic's own fiscal
// form, and the stamping code. The web cannot import @vysto/engine — it is not
// even a dependency, and the engine pulls `pg` — so a copy in the engine would
// have forced a second copy in the browser, and two copies of a SAT catalog
// drift and then disagree about whether a given clinic can be invoiced.
//
// Exposed as the browser-safe subpath `@vysto/shared/fiscal` rather than
// through the root barrel, which pulls jsonwebtoken/pino and crashes the
// browser. Pure data, no imports.
//
// MUST be `fiscal/index.ts`, not `fiscal/catalogs.ts`. The web's vite alias
// maps `@vysto/shared` straight at `packages/shared/src`, so the subpath has to
// resolve to a FILE or a directory with an index — pointing it at a bare
// directory fails the bundle with EISDIR. It did: typecheck, unit tests and the
// Docker build all passed, and only the browser bundle broke, which took the
// login page down and timed out every single e2e test with an error that named
// nothing about this file. Same family as the @vysto/shared barrel crash.

/** SAT `c_RegimenFiscal`, with which kind of taxpayer may hold each entry.
 *
 *  Taken from the live catalog (Facturama `GET /api/catalogs/FiscalRegimens`)
 *  rather than typed from memory. The `fisica`/`moral` flags are the only way
 *  to catch a persona-moral RFC paired with a persona-física régimen, which
 *  the PAC rejects at stamp time with no warning before that. */
export const FISCAL_REGIMENS = [
  { code: '601', name: 'General de Ley Personas Morales', fisica: false, moral: true },
  { code: '603', name: 'Personas Morales con Fines no Lucrativos', fisica: false, moral: true },
  {
    code: '605',
    name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios',
    fisica: true,
    moral: false,
  },
  { code: '606', name: 'Arrendamiento', fisica: true, moral: false },
  {
    code: '607',
    name: 'Régimen de Enajenación o Adquisición de Bienes',
    fisica: true,
    moral: false,
  },
  { code: '608', name: 'Demás ingresos', fisica: true, moral: false },
  {
    code: '610',
    name: 'Residentes en el Extranjero sin Establecimiento Permanente en México',
    fisica: true,
    moral: true,
  },
  {
    code: '611',
    name: 'Ingresos por Dividendos (socios y accionistas)',
    fisica: true,
    moral: false,
  },
  {
    code: '612',
    name: 'Personas Físicas con Actividades Empresariales y Profesionales',
    fisica: true,
    moral: false,
  },
  { code: '614', name: 'Ingresos por intereses', fisica: true, moral: false },
  {
    code: '615',
    name: 'Régimen de los ingresos por obtención de premios',
    fisica: true,
    moral: false,
  },
  { code: '616', name: 'Sin obligaciones fiscales', fisica: true, moral: false },
  {
    code: '620',
    name: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos',
    fisica: false,
    moral: true,
  },
  { code: '621', name: 'Incorporación Fiscal', fisica: true, moral: false },
  {
    code: '622',
    name: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras',
    fisica: false,
    moral: true,
  },
  { code: '623', name: 'Opcional para Grupos de Sociedades', fisica: false, moral: true },
  { code: '624', name: 'Coordinados', fisica: false, moral: true },
  {
    code: '625',
    name: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas',
    fisica: true,
    moral: false,
  },
  { code: '626', name: 'Régimen Simplificado de Confianza', fisica: true, moral: true },
] as const

/** SAT `c_UsoCFDI` — the subset a clinic paying for software would plausibly
 *  pick. `G03` is the right default: it is what a running expense uses. */
export const CFDI_USES = [
  { code: 'G03', name: 'Gastos en general' },
  { code: 'G01', name: 'Adquisición de mercancías' },
  { code: 'I04', name: 'Equipo de computación y accesorios' },
  { code: 'I08', name: 'Otra maquinaria y equipo' },
  { code: 'D10', name: 'Pagos por servicios educativos' },
  { code: 'S01', name: 'Sin efectos fiscales' },
  { code: 'CP01', name: 'Pagos' },
] as const

/** The generic RFC for público en general. Never a valid receptor for us — we
 *  invoice named clinics — but recognised so an error can say so. */
export const RFC_PUBLICO_EN_GENERAL = 'XAXX010101000'
